<template>
  <div v-click-outside="clickedOutside" v-element-loader="{ endpoint: 'product/search' }" class="auto-complete-search">
    <input
      v-model="searchText"
      type="text"
      class="auto-complete-search__input"
      :placeholder="t('quickOrderSearchPlaceholder')"
      @keydown="onSearchKeyDown"
      @focus="toggleSearchResult(true)">
    <button
      type="button"
      class="auto-complete-search__button">
      <oie-use-svg svg="close" class="auto-complete-search__icon"></oie-use-svg>
    </button>

    <div v-if="hasResults && showSearchResults" class="auto-complete-results" :class="{ 'auto-complete-results--has-hits': hasResults }">
      <div
        v-for="(product,i) in searchResult"
        :key="`auto-result-${i}`"
        :class="{ 'auto-complete-item__is-active': activeSearchItem === product }"
        class="auto-complete-result"
        @click="add(product.id, product.productId)">
        <oie-base-image
          :images="getAssetPath(productImage(product.image))"
          :alt-text="product.name"
          image-width="3.75rem"
          image-height="3.75rem"
          class="auto-complete-result__image"></oie-base-image>

        <div class="auto-complete-result__content">
          <div class="auto-complete-result__name">
            {{ product.name }}
          </div>

          <div class="auto-complete-result__sub-name">
            {{ product.id }}
          </div>
        </div>

        <oie-use-svg svg="close" color="#006DB6" class="auto-complete-result__icon"></oie-use-svg>
      </div>
    </div>
  </div>
</template>

<script>
import httpService from './../core/http-service'
import _ from 'lodash'

export default {
  props: {
    name: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    searchText: '',
    searchResult: [],
    showSearchResults: false,
    activeSearchResultIndex: -1
  }),
  computed: {
    hasResults() {
      return this.searchResult.length > 0
    },
    activeSearchItem() {
      return this.searchResult[this.activeSearchResultIndex]
    }
  },
  watch: {
    searchText: _.debounce(function (query) {
      const performSearch = query.length > 2
      if (performSearch) {
        this.search()
      }
      this.toggleSearchResult(performSearch)
    }, 100),
    showSearchResults(value) {
      if(!value) {
        this.activeSearchResultIndex = -1
      }
    }
  },
  methods: {
    toggleSearchResult(toggle) {
      this.showSearchResults = toggle
    },
    search() {
      return httpService.get('product/search', { query: this.searchText }).then(({ data }) => {
        this.searchResult = data.data
      })
    },
    clickedOutside() {
      this.toggleSearchResult(false)
    },
    add(id, productId) {
      this.publish(`fast-order-add-${this.name}`, { id, productId })
      this.searchText = ''
    },
    onSearchKeyDown(e) {
      switch (e.keyCode) {
        case 38:
          if (--this.activeSearchResultIndex < 0) {
            this.activeSearchResultIndex = this.searchResult.length - 1
          }
          break
        case 40:
          if (++this.activeSearchResultIndex === this.searchResult.length) {
            this.activeSearchResultIndex = 0
          }
          break
        case 13:
          if(this.activeSearchItem) {
            this.add(this.activeSearchItem.id)
          } else {
            const exactMatch = _.find(this.searchResult, i => i.id === this.searchText)
            if(exactMatch) {
              this.add(exactMatch.id)
            }
          }
          break
        case 27:
          this.toggleSearchResult(false)
          this.searchResult = []
          this.searchText = ''
          break
      }
    },
    productImage(image) {
      if (image) { return image + '?preset=ProductThumb&watermark=OIEWatermarkSmall' }
      return '/globalassets/missing404/404.png?preset=ProductThumb'
    }
  }
}
</script>

<style lang="scss">
.auto-complete-item__is-active {
  background-color: lightblue;
}

.auto-complete-search {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 25.9375rem;
}

.auto-complete-search__input {
  display: block;
  width: 100%;
  height: 3.125rem;
  padding: 0 2.5rem 0 measure(half);
  box-shadow: 0 0 4px 0 rgba(170, 170, 170, .5);
  border: none;
  border-radius: other(border-radius);
  &::-ms-clear{ display:none; }
}

.auto-complete-search__button {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  background-color: transparent;
  border: none;
  padding: 0 measure(quarter);

  &:hover {
    cursor: pointer;
  }
}

.auto-complete-search__icon {
  width: 1.25rem;
  height: 1.25rem;
  pointer-events: none;
  transform: rotate(-45deg);
}

.auto-complete-results {
  position: absolute;
  top: 100%;
  left: 0;
  width: 32.5rem;
  background-color: color(background-white);
  border-radius: 0 0 other(border-radius) other(border-radius);
  box-shadow: 0 0 5px 0 rgba(119, 119, 119, .5);
  padding: 0 measure(half);
}

.auto-complete-results--has-hits {
  display: flex;
  flex-direction: column;
  z-index: 10;
}

.auto-complete-result {
  @include body-smaller;
  position: relative;
  display: flex;
  align-content: center;
  border-bottom: 1px solid color(border-gray);
  padding: measure(half) measure(full) measure(half) 0;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    cursor: pointer;
  }
}

.auto-complete-result__content {
  display: flex;
  flex-direction: column;
  margin-left: measure(half);
}

.auto-complete-result__name {
  @include body-smaller-bold;
  color: color(text-blue);
}

.auto-complete-result:hover .auto-complete-result__name,
.auto-complete-result--is-selected .auto-complete-result__name {
  text-decoration: underline;
}

.auto-complete-result__icon {
  position: absolute;
  top: 50%;
  right: 0;
  width: 1.25rem;
  height: 1.25rem;
  pointer-events: none;
  transform: translateY(-50%) rotate(-45deg);
}
</style>
